import { parseDotNetDate } from 'utils/dotNetDate'

/**
 * Every channel on this screen hands its dates over already formatted, and no two agree:
 * "/Date(1690800000000)/", "2023-08-08T16:22:00", "08-Aug-2023 16:22", "17 Aug 2024 14:18:05",
 * "18-Aug-2023", "08/08/2023 16:22". The brief has to put them on one clock, so this reads all
 * of them - and returns null for anything it cannot read for certain.
 *
 * A null is a real answer. The brief never guesses a date: an entry it cannot place is listed
 * without one and takes no part in "last contact" or "overdue".
 */

const MONTHS: Record<string, number> = {
  jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5, jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
}

/** dd-MMM-yyyy, dd MMM yyyy, dd/MMM/yyyy - optional time, optional seconds, optional AM/PM. */
const NAMED_MONTH_RE =
  /^(\d{1,2})[-\s/]([A-Za-z]{3,9})[-\s/,]+(\d{4})(?:[\sT,]+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*([AaPp][Mm])?)?$/

/**
 * Numeric dates, in either order. The product formats day-first ("08/09/2026 04:30 PM"), but
 * several endpoints return .NET's untouched en-US default - "9/19/2026 5:16:16 PM", month first.
 * That default has a fixed fingerprint: slashes, seconds and AM/PM together. Where the numbers
 * themselves settle it (a 19 cannot be a month) they win over any fingerprint.
 */
const NUMERIC_DMY_RE =
  /^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})(?:[\sT,]+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*([AaPp][Mm])?)?$/

const ISO_RE = /^\d{4}-\d{2}-\d{2}(?:[T\s]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/

function hour24(hour: number, meridiem: string | undefined): number {
  if (!meridiem) return hour
  const pm = meridiem.toLowerCase() === 'pm'
  if (pm && hour < 12) return hour + 12
  if (!pm && hour === 12) return 0
  return hour
}

function build(
  year: number, month: number, day: number,
  hour = 0, minute = 0, second = 0,
): Date | null {
  if (month < 0 || month > 11 || day < 1 || day > 31 || hour > 23 || minute > 59 || second > 59) return null
  const date = new Date(year, month, day, hour, minute, second)
  // new Date(2023, 1, 31) rolls over to March - that is a misread, not a date.
  if (date.getMonth() !== month || date.getDate() !== day) return null
  return date
}

export function parseWhen(value: string | null | undefined): Date | null {
  if (value == null) return null
  const text = String(value).trim()
  if (text === '') return null

  if (text.startsWith('/Date(')) return parseDotNetDate(text)

  if (ISO_RE.test(text)) {
    const iso = new Date(text.replace(' ', 'T'))
    return Number.isNaN(iso.getTime()) ? null : iso
  }

  const named = NAMED_MONTH_RE.exec(text)
  if (named) {
    const month = MONTHS[named[2].slice(0, 3).toLowerCase()]
    if (month === undefined) return null
    return build(
      Number(named[3]), month, Number(named[1]),
      hour24(Number(named[4] ?? 0), named[7]), Number(named[5] ?? 0), Number(named[6] ?? 0),
    )
  }

  const numeric = NUMERIC_DMY_RE.exec(text)
  if (numeric) {
    const first = Number(numeric[1])
    const second = Number(numeric[2])
    const dotNetUsDefault = text.includes('/') && numeric[6] !== undefined && numeric[7] !== undefined
    const monthFirst = second > 12 || (first <= 12 && dotNetUsDefault)
    const [day, month] = monthFirst ? [second, first] : [first, second]
    return build(
      Number(numeric[3]), month - 1, day,
      hour24(Number(numeric[4] ?? 0), numeric[7]), Number(numeric[5] ?? 0), Number(numeric[6] ?? 0),
    )
  }

  return null
}

/** True when the text carried a clock time, so "due 18 Aug" is not reported as "due 18 Aug 00:00". */
export function hasClockTime(value: string | null | undefined): boolean {
  return /\d{1,2}:\d{2}/.test(String(value ?? ''))
}

const DAY_MS = 86_400_000

function startOfDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()
}

/** Whole calendar days from `from` to `to`; negative when `to` is earlier. */
export function calendarDaysBetween(from: Date, to: Date): number {
  return Math.round((startOfDay(to) - startOfDay(from)) / DAY_MS)
}

/** "today", "yesterday", "3 days ago", "4 months ago", "over 3 years ago", "tomorrow" - how a colleague would say it. */
export function relativeDay(when: Date, now: Date): string {
  const days = calendarDaysBetween(now, when)
  if (days === 0) return 'today'
  if (days === -1) return 'yesterday'
  if (days === 1) return 'tomorrow'
  // Past six weeks nobody counts days: "1132 days ago" is a sum the reader has to do.
  const span = spanInWords(Math.abs(days))
  return days < 0 ? `${span} ago` : `in ${span}`
}

function spanInWords(days: number): string {
  if (days <= 45) return `${days} days`
  const months = Math.round(days / 30.44)
  if (months < 24) return `${months} months`
  return `over ${Math.floor(days / 365.25)} years`
}

const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const pad = (n: number) => String(n).padStart(2, '0')

/** "08 Aug 2023, 16:22" - or "08 Aug 2023" when the source gave no time. */
export function formatWhen(when: Date, withTime: boolean): string {
  const day = `${pad(when.getDate())} ${SHORT_MONTHS[when.getMonth()]} ${when.getFullYear()}`
  return withTime ? `${day}, ${pad(when.getHours())}:${pad(when.getMinutes())}` : day
}
